import Cookies from 'cookies-js';
import moment from 'moment';
import isEmail from 'validator/lib/isEmail'

import ValidationError from './ValidationError';
import * as fetchHelpers from '@nhl/fetch-helpers';
import { getAuthToken } from '@nhl/auth-state-checker';

// NOTE: accountCookieData object comes from server and are set on AccountPage.mustache and SitePage.mustache
export const COOKIE_NAME = accountCookieData.ssoCookieName;
export const COOKIE_DOMAIN = accountCookieData.ssoCookieDomain;

const mapThingsToIdArray = things => things.map(thing => thing.id);

export const login = args => {
    const cleanArgs = {};

    Object.keys(args).forEach(key => {
        if (typeof args[key] === 'string') {
            cleanArgs[key] = args[key].trim();
        }
    });

    const errors = doValidation(['email', 'password', {
        message: 'Email incorrectly formatted',
        elements: ['email'],
        validationFunction: isEmail.bind(this, cleanArgs.email)
    }], cleanArgs);

    if (errors.length > 0) {
        return Promise.reject(errors);
    }

    return fetchHelpers.doDefaultFetchWithBody('/account/api/login', {
        isPublic: true,
        body: 'username=' + encodeURIComponent(cleanArgs.email) + '&password=' + encodeURIComponent(cleanArgs.password)
    }).then(data => {
        const expires = moment().add(data.expires_in, 'seconds');

        Cookies.set(COOKIE_NAME, data.access_token, {
            domain: COOKIE_DOMAIN,
            expires: expires.toDate()
        });
        return Promise.resolve(data);
    });
};

export const logout = args => {
    return fetchHelpers.doDefaultFetchWithBody('/account/api/logout', {
        isPublic: true,
        skipJSON: true,
        body: 'token=' + encodeURIComponent(getAuthToken()),
        ...args
    });
};

export const requestAccount = args => {
    const { profile } = args;
    const cleanProfile = {};
    
    Object.keys(profile).forEach(key => {
        cleanProfile[key] = profile[key].trim();
    });

    const errors = doValidation(['email', 'firstName', 'lastName', 'mediaAffiliation', 'title', {
        message: 'Email incorrectly formatted',
        elements: ['email'],
        validationFunction: isEmail.bind(this, cleanProfile.email)
    }], cleanProfile);

    if (errors.length > 0) {
        return Promise.reject(errors);
    }

    return fetchHelpers.doDefaultFetchWithBody('/account/api/request', {
        isPublic: true,
        contentType: fetchHelpers.CONTENT_TYPES.JSON,
        body: JSON.stringify(cleanProfile),
        ...args
    });
};

export const forgotPassword = args => {
    const { email } = args;

    const errors = doValidation(['email', {
        message: 'Email incorrectly formatted',
        elements: ['email'],
        validationFunction: isEmail.bind(this, email)
    }], { email });

    if (errors.length > 0) {
        return Promise.reject(errors);
    }

    return fetchHelpers.doDefaultFetchWithBody('/account/api/user/anon/password/forgot', {
        body: 'email=' + encodeURIComponent(email),
        isPublic: true,
        ...args
    });
};

export const fetchRequestedAccountInfo = args => {
    return fetchHelpers.doDefaultFetch(`/account/api/request/${args.validationCode}`, args);
};

export const register = args => {
    const { profile, validationCode } = args;
    const cleanProfile = {};
    const profileDataToSend = {};
    const allowedKeys = ['countryCode', 'postalCode', 'city', 'industries', 'address1', 'address2', 'password',
        'orgOutsideUsCa', 'stateProvinceCode'];

    Object.keys(profile).forEach(key => {
        if (typeof profile[key] === 'string') {
            cleanProfile[key] = profile[key].trim();
        } else {
            cleanProfile[key] = profile[key];
        }
    });

    const errors = doValidation(['email', 'password', 'confirmPassword', 'countryCode', {
        message: 'Passwords do not match',
        elements: ['password', 'confirmPassword'],
        validationFunction: checkValuesEqual.bind(this, cleanProfile.password, cleanProfile.confirmPassword)
    }, {
        message: 'Passwords must be at least 8 characters long',
        elements: ['password', 'confirmPassword'],
        validationFunction: () => checkPasswordLength(cleanProfile.password || '') && checkPasswordLength(cleanProfile.confirmPassword || '')
    }], cleanProfile);

    if (!validationCode) {
        errors.push(new ValidationError('We could not find your validation code. Please follow the link in the email which was sent to you.'));
    }

    if (errors.length > 0) {
        return Promise.reject(errors);
    }

    Object.keys(cleanProfile).forEach(key => {
        if (allowedKeys.includes(key)) {
            if (key === 'industries') {
                profileDataToSend[key] = mapThingsToIdArray(cleanProfile[key]);
            } else {
                profileDataToSend[key] = cleanProfile[key];
            }
        }
    });

    return fetchHelpers.doDefaultFetchWithBody(`/account/api/user?code=${args.validationCode}`, {
        isPublic: true,
        contentType: fetchHelpers.CONTENT_TYPES.JSON,
        body: JSON.stringify(profileDataToSend),
        ...args
    });
};

export const fetchAccountInfo = args => {
    return fetchHelpers.doDefaultFetch('/account/rest/api/user/me?include=industries&include=subscriptionTeams&' +
        'include=subscriptionCategories', args);
};

export const saveProfile = args => {
    const { profile } = args;
    const cleanProfile = {};

    Object.keys(profile).forEach(key => {
        if (typeof profile[key] === 'string') {
            cleanProfile[key] = profile[key].trim();
        } else {
            cleanProfile[key] = profile[key];
        }
    });

    if (args.validateProfile) {
        const errors = doValidation(['email', 'firstName' , 'lastName', 'mediaAffiliation', 'title', 'countryCode', {
            message: 'Email incorrectly formatted',
            elements: ['email'],
            validationFunction: isEmail.bind(this, cleanProfile.email)
        }], cleanProfile);

        if (errors.length > 0) {
            return Promise.reject(errors);
        }
    }

    if (cleanProfile.subscriptionTeams) {
        cleanProfile.subscriptionTeams = mapThingsToIdArray(cleanProfile.subscriptionTeams);
    }

    if (cleanProfile.subscriptionCategories) {
        cleanProfile.subscriptionCategories = mapThingsToIdArray(cleanProfile.subscriptionCategories);
    }
    
    if (cleanProfile.industries) {
        cleanProfile.industries = mapThingsToIdArray(cleanProfile.industries);
    }

    return fetchHelpers.doDefaultFetchWithBody('/account/rest/api/user/me', {
        method: 'PUT',
        contentType: fetchHelpers.CONTENT_TYPES.JSON,
        body: JSON.stringify(cleanProfile),
        ...args
    });
};

export const updatePassword = args => {
    const { passwordData, isReset, validationCode } = args;
    const cleanPasswordData = {};

    Object.keys(passwordData).forEach(key => {
        cleanPasswordData[key] = passwordData[key].trim();
    });

    const validationArray = ['newPassword', 'confirmPassword', {
        message: 'Passwords do not match',
        elements: ['newPassword', 'confirmPassword'],
        validationFunction: checkValuesEqual.bind(this, cleanPasswordData.newPassword, cleanPasswordData.confirmPassword)
    }, {
        message: 'Passwords must be at least 8 characters long',
        elements: ['newPassword', 'confirmPassword'],
        validationFunction: () => checkPasswordLength(cleanPasswordData.newPassword) && checkPasswordLength(cleanPasswordData.confirmPassword)
    }];

    if (!isReset) {
        validationArray.push('currentPassword');
    }

    const errors = doValidation(validationArray, cleanPasswordData);

    if (isReset && !validationCode) {
        errors.push(new ValidationError('We could not find your validation code. Please follow the link in the email which was sent to you.'));
    }

    if (errors.length > 0) {
        return Promise.reject(errors);
    }

    if (!isReset) {
        return fetchHelpers.doDefaultFetchWithBody('/account/rest/api/user/me/password/change', {
            body: 'currentPassword=' + encodeURIComponent(cleanPasswordData.currentPassword) +
                '&newPassword=' + encodeURIComponent(cleanPasswordData.newPassword),
            ...args
        });
    } else {
        return fetchHelpers.doDefaultFetchWithBody('/account/api/user/anon/password/change', {
            isPublic: true,
            body: 'validationCode=' + encodeURIComponent(validationCode) + '&newPassword=' +
                encodeURIComponent(cleanPasswordData.newPassword),
            ...args
        });
    }
};

export const fetchIndustryTypes = args => {
    return fetchHelpers.doDefaultFetch('/account/' + (!args.isPublic ? 'rest/' : '') + 'api/industrytype', args);
};

export const fetchEmailSubscriptionCategories = args => {
    return fetchHelpers.doDefaultFetch('/account/rest/api/subscriptioncategory', args);
};

const checkValuesEqual = (a, b) => a === b;
const checkPasswordLength = password => password.length >= 8;

const doValidation = (fields, data) => {
    const errors = [];

    fields.forEach(field => {
        const defaultFieldArgs = {
            message: 'This field is required',
            elementMessage: ''
        };
        const fieldArgs = {};

        if (typeof field === 'string') {
            Object.assign(fieldArgs, defaultFieldArgs);
            fieldArgs.name = field;
            fieldArgs.elements = [field]
        } else {
            Object.assign(fieldArgs, defaultFieldArgs, field);
        }

        if (fieldArgs.validationFunction && !fieldArgs.validationFunction()) {
            errors.push(new ValidationError(fieldArgs.message, fieldArgs.elementMessage, ...fieldArgs.elements));
        } else if (!fieldArgs.validationFunction && !data[fieldArgs.name]) {
            errors.push(new ValidationError(fieldArgs.message, fieldArgs.elementMessage, ...fieldArgs.elements));
        }
    });

    return errors;
};